import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Smartphone, Apple, Download } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface WalletSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  passType: 'visit' | 'referral'
}

export const WalletSelectionModal = ({ isOpen, onClose, passType }: WalletSelectionModalProps) => {
  const [isGenerating, setIsGenerating] = useState<'apple' | 'google' | null>(null)
  const { toast } = useToast()

  const detectPlatform = () => {
    const userAgent = navigator.userAgent.toLowerCase()
    const isIOS = /iphone|ipad|ipod/.test(userAgent)
    const isAndroid = /android/.test(userAgent)
    return { isIOS, isAndroid }
  }

  const { isIOS, isAndroid } = detectPlatform()

  const handleAppleWallet = async () => {
    setIsGenerating('apple')
    
    try {
      const { data: session } = await supabase.auth.getSession()
      
      if (!session?.session?.access_token) {
        throw new Error('Nicht angemeldet')
      }

      const response = await fetch('https://zixinoptmgfaswwbjjgi.supabase.co/functions/v1/generate-pkpass', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ passType })
      })

      if (!response.ok) {
        let errorMessage = `Pass konnte nicht generiert werden (${response.status})`
        let errorType = 'unknown'
        
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = errorData.error
            if (errorMessage.includes('Customer lookup failed') || errorMessage.includes('Customer not found')) {
              errorType = 'customer_not_found'
            } else if (errorMessage.includes('Authentication failed')) {
              errorType = 'auth_error'
            } else if (errorMessage.includes('Tenant not found')) {
              errorType = 'tenant_error'
            }
          }
        } catch {
          // Fallback error handling
          if (response.status === 401) {
            errorType = 'auth_error'
            errorMessage = 'Authentifizierung fehlgeschlagen'
          } else if (response.status === 404) {
            errorType = 'customer_not_found'
            errorMessage = 'Kunde nicht gefunden'
          }
        }
        
        const error = new Error(errorMessage) as Error & { type?: string }
        error.type = errorType
        throw error
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${passType}-pass.pkpass`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      toast({
        title: "Apple Wallet Pass erstellt",
        description: "Pass wurde heruntergeladen. Tippen Sie darauf, um ihn zu Apple Wallet hinzuzufügen."
      })
      onClose()
    } catch (error) {
      console.error('Apple Wallet Error:', error)
      
      const errorWithType = error as Error & { type?: string }
      let title = "Apple Wallet Fehler"
      let description = 'Pass konnte nicht erstellt werden.'
      
      if (errorWithType.type === 'customer_not_found') {
        title = "Registrierung erforderlich"
        description = 'Sie müssen als Kunde registriert sein, um einen Pass zu erstellen.'
      } else if (errorWithType.type === 'auth_error') {
        title = "Anmeldung erforderlich"
        description = 'Bitte melden Sie sich erneut an.'
      } else if (errorWithType.type === 'tenant_error') {
        title = "Konfigurationsfehler"
        description = 'Tenant-Konfiguration nicht gefunden. Kontaktieren Sie den Support.'
      } else {
        description = errorWithType.message || description
      }
      
      toast({
        title,
        description,
        variant: "destructive"
      })
    } finally {
      setIsGenerating(null)
    }
  }

  const handleGooglePay = async () => {
    setIsGenerating('google')
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-google-pay-pass', {
        body: { passType }
      })

      if (error) {
        throw error
      }

      if (data?.saveUrl) {
        window.open(data.saveUrl, '_blank')
        toast({
          title: "Google Pay Pass erstellt",
          description: "Google Pay wird in einem neuen Fenster geöffnet..."
        })
        onClose()
      } else {
        throw new Error('Keine Save-URL erhalten')
      }
      
    } catch (error) {
      console.error('Google Pay Error:', error)
      
      let title = "Google Pay Fehler"
      let description = 'Fehler beim Erstellen der Google Pay Karte'
      
      const errorMessage = error instanceof Error ? error.message : ''
      
      if (errorMessage.includes('Customer lookup failed') || errorMessage.includes('Customer not found')) {
        title = "Registrierung erforderlich"
        description = 'Sie müssen als Kunde registriert sein, um einen Pass zu erstellen.'
      } else if (errorMessage.includes('Authentication failed')) {
        title = "Anmeldung erforderlich"
        description = 'Bitte melden Sie sich erneut an.'
      } else if (errorMessage.includes('Edge Function')) {
        title = "Server Fehler"
        description = 'Temporärer Server-Fehler. Bitte versuchen Sie es erneut.'
      } else if (errorMessage) {
        description = errorMessage
      }
      
      toast({
        title,
        description,
        variant: "destructive"
      })
    } finally {
      setIsGenerating(null)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Zu Wallet hinzufügen
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            Wählen Sie Ihre bevorzugte Wallet-App:
          </p>
          
          <div className="grid gap-3">
            {/* Apple Wallet Option */}
            <Button
              onClick={handleAppleWallet}
              disabled={!!isGenerating}
              className="flex items-center justify-center gap-3 h-12 bg-black hover:bg-gray-800 text-white"
            >
              <Apple className="h-5 w-5" />
              <span className="font-medium">
                {isGenerating === 'apple' ? 'Wird erstellt...' : 'Apple Wallet'}
              </span>
              {isIOS && <span className="text-xs bg-green-600 px-2 py-1 rounded">Empfohlen</span>}
            </Button>

            {/* Google Pay Option */}
            <Button
              onClick={handleGooglePay}
              disabled={!!isGenerating}
              className="flex items-center justify-center gap-3 h-12 bg-[#4285f4] hover:bg-[#3367d6] text-white"
            >
              <Smartphone className="h-5 w-5" />
              <span className="font-medium">
                {isGenerating === 'google' ? 'Wird erstellt...' : 'Google Pay'}
              </span>
              {isAndroid && <span className="text-xs bg-green-600 px-2 py-1 rounded">Empfohlen</span>}
            </Button>
          </div>

          <div className="text-xs text-muted-foreground space-y-1">
            <p>💡 <strong>Hinweis:</strong></p>
            <p>• Apple Wallet: Lädt eine .pkpass Datei herunter</p>
            <p>• Google Pay: Öffnet direkt in der Google Pay App</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default WalletSelectionModal